/**
 * External Imports
 */
import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { format, subDays } from 'date-fns';

/**
 * Nexus Imports
 */
import { NeuCheckbox, NeuImage } from '@neutron/react';

/**
 * Internal Imports
 */
import FilterCard from '../reusableModalPieces/modalCard/ModalCard';
import DateSelectsSection from '../reusableModalPieces/dateSelectsSection/DateSelectsSection';
import PatientSearch from '../reusableModalPieces/patientSearch';
import EmployeeSearch from '../reusableModalPieces/employeeSearch';
import { Dropdown } from '../../shared/dropdowns';
import AdminFilterSection from './adminFilterSection/AdminFilterSection';
import RoundsReportFilterSection from './roundsReportFilterSection';
import ComplianceFilterSection from './complianceReportFilterSection';
import EmployeeReportsFilterSection from './employeeReportsFilterSection';

import { RootState } from '../../../redux/store';
import {
  setAdminUserFilter,
  clearAdminUserFilter,
  openModal,
  setModalType,
  clearRoundsReportFilter,
  setRoundsReportFilter,
  clearStoplightFilter,
  setStoplightFilter,
  getCategoriesByType,
  setFeedbackFilter,
  setIssueFilter,
  clearFeedbackFilter,
  clearIssueFilter,
  setpatientSummaryReportFilter,
  setComplianceReportFilter,
  clearPatientSummaryReportFilter,
  clearComplianceReportFilter,
  clearCensusReportFilter,
  setCensusReportFilter,
  clearEmployeeSummaryReportFilter,
  setEmployeeSummaryReportFilter,
  setFrequencyReportFilter,
  clearFrequencyReportFilter,
  setEmployeeShiftStatusFilter,
  clearEmployeeShiftStatusFilter
} from '../../../redux/actions';

import { compareObjByStringKey, unCamelCase } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  Category,
  Employee,
  Filter,
  Patient
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { InputHeading, StatusContainer } from './FilterModal.styles';

/**
 * Static Imports
 */
import serviceCentralLogoImg from '../../../assets/images/service_central_icon.png';

/**
 * Constant Imports
 */
import serviceCentralCategories from '../../../constants/serviceCentralCategories.json';
import roundingTypes from '../../../constants/validRoundingTypes.json';

interface FilterProps {
  filterType: string;
  adminUserFilter: Filter;
  censusReportFilter: Filter;
  complianceReportFilter: Filter;
  employeeSummaryReportFilter: Filter;
  employeeShiftStatus: string;
  feedbackFilter: Filter;
  frequencyReportFilter: Filter;
  issueFilter: Filter;
  location: string;
  patientSummaryReportFilter: Filter;
  roundsReportFilter: Filter;
  stoplightFilter: Filter;
  taskCategories: Category[];
}

const filters: Filter = {
  // TODO: Group these by filter
  startDate: '',
  endDate: format(new Date(), 'yyyy-MM-dd'),
  category: '',
  subCategory: '',
  creator: '',
  createdBy: '',
  patient: '',
  employee: '',
  roundedBy: [],
  employeeId: '',
  userId: '',
  complete: false,
  opened: false,
  deferred: false,
  // admin params
  userRole: '',
  accessLevel: '',
  accessPlace: '',
  // including the below parameters for when they exist in the future for issues
  attachedToStoplight: false,
  hasAttachedIssues: false,
  hasUpdate: false,
  dateRangeText: '',
  template: '',
  taskSubType: ''
};
const censusStatus = [
  { value: 'rounded', displayName: 'Rounded' },
  { value: 'unable', displayName: 'Unable' },
  { value: 'none', displayName: 'None' }
];

const employeeShiftStatusOptions = [
  { value: 'all', displayName: 'All Nurses' },
  { value: 'onShift', displayName: 'Nurses Clocked In' },
  { value: 'offShift', displayName: 'Nurses Clocked Out' }
];

const FilterModal: FC<FilterProps> = ({
  filterType,
  adminUserFilter,
  censusReportFilter,
  complianceReportFilter,
  employeeShiftStatus,
  employeeSummaryReportFilter,
  feedbackFilter,
  frequencyReportFilter,
  issueFilter,
  location,
  patientSummaryReportFilter,
  roundsReportFilter,
  stoplightFilter,
  taskCategories
}) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const [filterObject, setFilterObject] = useState<Filter>(filters);
  const [shiftStatus, setShiftStatus] = useState<string>(employeeShiftStatus);
  const [openCatDropdown, setOpenCatDropdown] = useState(false);
  const [openSubcatDropdown, setOpenSubcatDropdown] = useState(false);
  const [isCensusDateMoreThan7Days, setIsCensusDateMoreThan7Days] =
    useState(false);
  const [openCensusStatusDropdown, setOpenCensusStatusDropdown] =
    useState(false);
  const [openEmployeeShiftStatusDropdown, setOpenEmployeeShiftStatusDropdown] =
    useState(false);

  const today = new Date();

  useEffect(() => {
    if (pathname.includes('reports/patient')) {
      return setFilterObject({
        ...filterObject,
        startDate: format(new Date(), 'yyyy-MM-dd'),
        dateRangeText: 'Today'
      });
    }
    if (pathname.includes('reports/employee')) {
      return setFilterObject({
        ...filterObject,
        startDate: format(
          new Date(new Date().getFullYear(), new Date().getMonth(), 1),
          'yyyy-MM-dd'
        ),
        dateRangeText: 'Month to date'
      });
    }
    return setFilterObject({
      ...filterObject,
      startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd')
    });
  }, []);

  const handleCategorySelect = (value: string) => {
    setFilterObject({
      ...filterObject,
      category: value,
      subCategory: ''
    });
    setOpenCatDropdown(false);
  };

  const handleCensusStatusSelect = (value: string) => {
    setFilterObject({ ...filterObject, status: value });
    setOpenCensusStatusDropdown(false);
  };

  const handleEmployeeShiftStatusSelect = (value: string) => {
    setShiftStatus(value);
    setOpenEmployeeShiftStatusDropdown(false);
  };

  const handleSubcategorySelect = (value: string) => {
    setFilterObject({ ...filterObject, subCategory: value });
    setOpenSubcatDropdown(false);
  };

  // handling checkbox states differently because of onChange conflicts
  const handleCheckboxes = (name: string) => {
    setFilterObject({ ...filterObject, [name]: !filterObject[name] });
  };

  const handleFeedbackTypeCheckboxes = (
    e: React.BaseSyntheticEvent<MouseEvent>
  ) => {
    const { name } = e.target;
    setFilterObject({ ...filterObject, taskSubType: name });
  };

  // handling select & date inputs
  const handleDropdownChange = useCallback(
    (value: string, name: string) => {
      // if Master access is chosen on admin filter, input HCA value for access place
      if (name === 'accessLevel' && value === 'master') {
        setFilterObject({ ...filterObject, [name]: value, accessPlace: 'HCA' });
      } else {
        setFilterObject({ ...filterObject, [name]: value });
      }
    },
    [filterObject]
  );

  // handling select & date inputs
  const handleInputs = (e: CustomEvent<any>) => {
    const { name, value } = e.target as HTMLInputElement;
    if (value) {
      setFilterObject({
        ...filterObject,
        [name]: value
      });
    }
  };

  const handleReOpen = () => {
    if (filterType === 'issues') {
      dispatch(setModalType('Filter Issues'));
    } else if (filterType === 'feedback') {
      dispatch(setModalType('Filter Feedback'));
    } else if (filterType === 'stoplights') {
      dispatch(setModalType('Filter Stoplights'));
    } else if (filterType === 'adminUsers') {
      dispatch(setModalType('Filter Admin Users'));
    } else if (filterType === 'subjectsToRoundList') {
      dispatch(setModalType('Filter Subjects To Round List'));
    } else if (filterType === 'roundsReport') {
      dispatch(setModalType('Filter Rounds Report'));
    } else if (filterType === 'complianceReport') {
      dispatch(setModalType('Filter Compliance Report'));
    } else if (filterType === 'summaryReport') {
      dispatch(setModalType('Filter Summary Report'));
    } else if (filterType === 'employeeSummaryReport') {
      dispatch(setModalType('Filter Employee Summary Report'));
    } else if (filterType === 'censusReport') {
      dispatch(setModalType('Filter Census Report'));
    } else if (filterType === 'frequencyReport') {
      dispatch(setModalType('Filter Frequency Report'));
    }
    setFilterObject(filters);
    dispatch(openModal());
  };

  const handleRemoveChip = (type: string, empType?: string) => {
    if (type === 'patient') {
      setFilterObject({ ...filterObject, patient: '' });
    } else {
      setFilterObject({
        ...filterObject,
        ...(empType === 'creator'
          ? { creator: '', createdBy: '' }
          : { employee: '', employeeId: '' })
      });
    }
  };

  const clearFilterOptions = () => {
    if (filterType === 'stoplights') {
      dispatch(clearStoplightFilter());
    } else if (filterType === 'issues') {
      dispatch(clearIssueFilter());
    } else if (filterType === 'feedback') {
      dispatch(clearFeedbackFilter());
    } else if (filterType === 'subjectsToRoundList') {
      setShiftStatus('');
    }
    dispatch(clearAdminUserFilter());
    dispatch(clearRoundsReportFilter());
    dispatch(clearComplianceReportFilter());
    dispatch(clearPatientSummaryReportFilter());
    dispatch(clearEmployeeSummaryReportFilter());
    dispatch(clearFrequencyReportFilter());
    dispatch(clearCensusReportFilter());
    dispatch(clearEmployeeShiftStatusFilter());
    setFilterObject(filters);
    handleReOpen();
  };

  const setPatientSelection = (patient: Patient) => {
    setFilterObject({
      ...filterObject,
      patient: `${patient.firstname} ${patient.lastname}`
    });
  };

  const setEmployeeSelection = (name: string, emp: Employee) => {
    setFilterObject({
      ...filterObject,
      [name]: `${emp.firstName} ${emp.lastName}`,
      [name === 'employee' ? 'employeeId' : 'createdBy']: emp.employeeId
    });
  };

  const setFilters = () => {
    if (filterType === 'issues') {
      dispatch(setIssueFilter(filterObject));
    } else if (filterType === 'feedback') {
      dispatch(setFeedbackFilter(filterObject));
    } else if (filterType === 'stoplights') {
      dispatch(setStoplightFilter(filterObject));
    } else if (filterType === 'adminUsers') {
      dispatch(setAdminUserFilter(filterObject));
    } else if (filterType === 'roundsReport') {
      dispatch(setRoundsReportFilter(filterObject));
    } else if (filterType === 'complianceReport') {
      dispatch(setComplianceReportFilter(filterObject));
    } else if (filterType === 'summaryReport') {
      dispatch(setpatientSummaryReportFilter(filterObject));
    } else if (filterType === 'employeeSummaryReport') {
      dispatch(setEmployeeSummaryReportFilter(filterObject));
    } else if (filterType === 'censusReport') {
      dispatch(setCensusReportFilter(filterObject));
    } else if (filterType === 'frequencyReport') {
      dispatch(setFrequencyReportFilter(filterObject));
    } else if (filterType === 'subjectsToRoundList') {
      dispatch(setEmployeeShiftStatusFilter(shiftStatus));
    }
  };

  const populateFilter = () => {
    if (Object.keys(roundsReportFilter).length > 0) {
      setFilterObject(roundsReportFilter);
    }
    if (Object.keys(censusReportFilter).length > 0) {
      setFilterObject(censusReportFilter);
    }
    if (Object.keys(frequencyReportFilter).length > 0) {
      setFilterObject(frequencyReportFilter);
    }
    if (Object.keys(complianceReportFilter).length > 0) {
      setFilterObject(complianceReportFilter);
    }
    if (
      Object.keys(patientSummaryReportFilter).length > 0 &&
      filterType === 'summaryReport'
    ) {
      setFilterObject(patientSummaryReportFilter);
    }
    if (
      Object.keys(employeeSummaryReportFilter).length > 0 &&
      filterType === 'employeeSummaryReport'
    ) {
      setFilterObject(employeeSummaryReportFilter);
    }
    if (Object.keys(adminUserFilter).length > 0) {
      setFilterObject(adminUserFilter);
    }
    if (filterType === 'issues') {
      setFilterObject({
        ...filterObject,
        ...issueFilter,
        startDate:
          issueFilter.startDate || format(subDays(today, 30), 'yyyy-MM-dd'),
        endDate: issueFilter.endDate || format(today, 'yyyy-MM-dd')
      });
    } else if (filterType === 'feedback') {
      setFilterObject({
        ...filterObject,
        ...feedbackFilter,
        startDate:
          feedbackFilter.startDate || format(subDays(today, 30), 'yyyy-MM-dd'),
        endDate: feedbackFilter.endDate || format(today, 'yyyy-MM-dd')
      });
    } else if (filterType === 'stoplights') {
      setFilterObject({
        ...filterObject,
        ...stoplightFilter,
        startDate:
          stoplightFilter.startDate || format(subDays(today, 30), 'yyyy-MM-dd'),
        endDate: stoplightFilter.endDate || format(today, 'yyyy-MM-dd')
      });
    }
  };

  // TODO: Do we need to remove dups [...new Set(feedbackOptions)]
  const categoryNames = useMemo(
    () =>
      [...taskCategories]
        .sort(compareObjByStringKey('name'))
        .map(cat => cat.name),
    [taskCategories]
  );

  const subcategoryNames = useMemo(
    () =>
      [
        ...(taskCategories.find(
          (cat: Category) => filterObject.category === cat.name
        )?.subCategories || [])
      ].sort(),
    [taskCategories, filterObject.category]
  );

  useEffect(() => {
    // if a filter existing populate it
    populateFilter();
  }, [issueFilter, feedbackFilter, stoplightFilter]);

  // mapping the values out of the categories for feedback & removing duplicates
  useEffect(() => {
    if (
      location &&
      filterType &&
      roundingTypes.includes(location) &&
      (filterType === 'feedback' ||
        filterType === 'issues' ||
        filterType === 'stoplights')
    ) {
      let categoryFor;
      switch (location) {
        case 'patient':
          categoryFor = 'patient';
          break;
        default:
          categoryFor = 'employee';
          break;
      }
      const params = {
        categoryFor,
        categoryType: filterType.replace(/s$/, '')
        // TODO: Filter categories by type ?
        // ...(filterObject.type ? { feedbackType: filterObject.type } : {})
      };
      dispatch(getCategoriesByType(params));
    }
  }, [filterType, location]);

  return (
    <FilterCard
      headerIcon="filter_list"
      headerTitle="Filter"
      modalRightBtnText="Apply Filters"
      modalRightBtnAction={setFilters}
      modalLeftBtnText="Clear Filters"
      modalLeftBtnAction={clearFilterOptions}
      isApplyFiltersDisabled={isCensusDateMoreThan7Days}
    >
      {filterType === 'adminUsers' ? (
        <AdminFilterSection
          filterValues={filterObject}
          handleDropdownChange={handleDropdownChange}
        />
      ) : filterType === 'roundsReport' || filterType === 'censusReport' ? (
        <>
          <RoundsReportFilterSection
            filterValues={filterObject}
            type={pathname.includes('patient') ? 'patient' : 'employee'}
            roundsReportType={pathname.includes('census') ? 'census' : ''}
            setFilterValues={setFilterObject}
            handleRemoveChip={handleRemoveChip}
            setIsCensusDateMoreThan7Days={setIsCensusDateMoreThan7Days}
          />
          {pathname.includes('/reports/employee') && (
            <EmployeeReportsFilterSection
              filterValues={filterObject}
              setFilterValues={setFilterObject}
            />
          )}
          {pathname.includes('census') && (
            <>
              <InputHeading>Status</InputHeading>
              <Dropdown
                name="date-range"
                classes="ps-0"
                open={openCensusStatusDropdown}
                placeholder={
                  (filterObject.status && unCamelCase(filterObject.status)) ??
                  'Select Status'
                }
                setOpen={setOpenCensusStatusDropdown}
              >
                {censusStatus.map(status => (
                  <div
                    key={status.value}
                    aria-selected={
                      unCamelCase(status.value) === filterObject.status
                    }
                    className={`dropdown-hover-item${
                      unCamelCase(status.value) === filterObject.status
                        ? ' selected'
                        : ''
                    }`}
                    role="option"
                    tabIndex={0}
                    onClick={() => handleCensusStatusSelect(status.value)}
                    onKeyDown={() => handleCensusStatusSelect(status.value)}
                  >
                    {status.displayName}
                  </div>
                ))}
              </Dropdown>
            </>
          )}
        </>
      ) : (pathname.includes('/reports/patient') &&
          filterType === 'complianceReport') ||
        filterType === 'summaryReport' ? (
        <ComplianceFilterSection
          filterValues={filterObject}
          setFilterValues={setFilterObject}
        />
      ) : filterType === 'employeeSummaryReport' ||
        filterType === 'complianceReport' ||
        pathname.includes('/reports/employee') ? (
        <EmployeeReportsFilterSection
          filterValues={filterObject}
          setFilterValues={setFilterObject}
        />
      ) : filterType === 'subjectsToRoundList' ? (
        <>
          <InputHeading>Show results for</InputHeading>
          <Dropdown
            name="date-range"
            classes="ps-0"
            open={openEmployeeShiftStatusDropdown}
            placeholder={
              employeeShiftStatusOptions.find(
                option => option.value === shiftStatus
              )?.displayName ?? 'Select Status'
            }
            setOpen={setOpenEmployeeShiftStatusDropdown}
          >
            {employeeShiftStatusOptions.map(status => (
              <div
                key={status.value}
                aria-selected={status.value === shiftStatus}
                className={`dropdown-hover-item${
                  status.value === shiftStatus ? ' selected' : ''
                }`}
                role="option"
                tabIndex={0}
                onClick={() => handleEmployeeShiftStatusSelect(status.value)}
                onKeyDown={() => handleEmployeeShiftStatusSelect(status.value)}
              >
                {status.displayName}
              </div>
            ))}
          </Dropdown>
        </>
      ) : (
        <>
          <DateSelectsSection
            handleSelects={handleInputs}
            startDate={filterObject.startDate}
            endDate={filterObject.endDate}
            style={{ marginTop: '10px' }}
          />
          <div id="Modal-Category-Select">
            <InputHeading>Category</InputHeading>
            <Dropdown
              classes="ps-0"
              name="category"
              open={openCatDropdown}
              placeholder={
                filterObject.category
                  ? filterObject.category
                  : 'Select a Category'
              }
              setOpen={setOpenCatDropdown}
            >
              {categoryNames.map((category: string) => (
                <div
                  key={category}
                  className={`dropdown-hover-item${
                    filterObject.category === category ? ' selected' : ''
                  }`}
                  aria-selected={filterObject.category === category}
                  role="option"
                  tabIndex={0}
                  onClick={() => handleCategorySelect(category)}
                  onKeyDown={() => handleCategorySelect(category)}
                >
                  <div className="d-flex align-items-center">
                    {category}
                    {filterType === 'issues' &&
                      serviceCentralCategories.includes(category) && (
                        <NeuImage
                          width="25"
                          alt="service central logo"
                          src={serviceCentralLogoImg}
                        />
                      )}
                  </div>
                </div>
              ))}
            </Dropdown>
          </div>
          <div id="Modal-Subcategory-Select">
            <InputHeading>Subcategory</InputHeading>
            <Dropdown
              classes="ps-0"
              disabled={!filterObject.category || subcategoryNames.length === 0}
              name="subcategory"
              open={openSubcatDropdown}
              placeholder={
                filterObject.subCategory ||
                (filterObject.category &&
                  subcategoryNames.length === 0 &&
                  'No subcategories') ||
                'Select a Subcategory'
              }
              setOpen={setOpenSubcatDropdown}
            >
              {subcategoryNames.map((subcategory: string) => (
                <div
                  key={subcategory}
                  className={`dropdown-hover-item${
                    filterObject.subCategory === subcategory ? ' selected' : ''
                  }`}
                  aria-selected={filterObject.subCategory === subcategory}
                  role="option"
                  tabIndex={0}
                  onClick={() => handleSubcategorySelect(subcategory)}
                  onKeyDown={() => handleSubcategorySelect(subcategory)}
                >
                  {subcategory}
                </div>
              ))}
            </Dropdown>
          </div>
          {filterType !== 'stoplights' &&
            (location === 'patient' ? (
              filterType === 'issues' ? (
                <PatientSearch
                  chipVal={filterObject.patient}
                  val={filterObject.patient ?? ''}
                  handleRemovePatient={handleRemoveChip}
                  setPatientSelection={(patient: Patient) =>
                    setPatientSelection(patient)
                  }
                />
              ) : (
                <>
                  <PatientSearch
                    chipVal={filterObject.patient}
                    val={filterObject.patient ?? ''}
                    handleRemovePatient={handleRemoveChip}
                    setPatientSelection={(patient: Patient) =>
                      setPatientSelection(patient)
                    }
                  />
                  <EmployeeSearch
                    chipVal={filterObject.employee}
                    val={filterObject.employeeId}
                    handleRemoveEmployee={(type: string) =>
                      handleRemoveChip(type, 'employee')
                    }
                    setEmployeeSelection={(emp: Employee) =>
                      setEmployeeSelection('employee', emp)
                    }
                  />
                </>
              )
            ) : (
              <EmployeeSearch
                chipVal={filterObject.employee}
                val={filterObject.employeeId}
                handleRemoveEmployee={(type: string) =>
                  handleRemoveChip(type, 'employee')
                }
                setEmployeeSelection={(emp: Employee) =>
                  setEmployeeSelection('employee', emp)
                }
              />
            ))}
          <InputHeading>Created By</InputHeading>
          <EmployeeSearch
            chipVal={filterObject.creator}
            showLabel={false}
            val={filterObject.createdBy}
            handleRemoveEmployee={(type: string) =>
              handleRemoveChip(type, 'creator')
            }
            setEmployeeSelection={(emp: Employee) =>
              setEmployeeSelection('creator', emp)
            }
          />
          <InputHeading>Status</InputHeading>
          <StatusContainer id="Filter-Modal-Status-Container">
            <NeuCheckbox
              data-testid={`filter-modal-status-${
                filterType === 'stoplights' ? 'in-progress' : 'opened'
              }-checkbox`}
              className="mt-0"
              label={filterType === 'stoplights' ? 'In Progress' : 'Opened'}
              name="opened"
              checked={filterObject.opened}
              color="blue"
              onNeuChange={() => handleCheckboxes('opened')}
            />
            <NeuCheckbox
              data-testid="filter-modal-status-complete-checkbox"
              className="mt-0"
              label="Completed"
              name="complete"
              checked={filterObject.complete}
              color="blue"
              onNeuChange={() => handleCheckboxes('complete')}
            />

            {filterType === 'stoplights' && (
              <NeuCheckbox
                data-testid="filter-modal-status-deferred-checkbox"
                className="mt-0"
                label="Deferred"
                name="deferred"
                checked={filterObject.deferred}
                color="blue"
                onNeuChange={() => handleCheckboxes('deferred')}
              />
            )}
          </StatusContainer>
          {filterType === 'feedback' && (
            <StatusContainer id="Filter-Modal-Status-Container">
              <InputHeading>Feedback Type</InputHeading>
              <NeuCheckbox
                label="Recognition"
                className="mt-0"
                name="recognition"
                color="blue"
                checked={filterObject.taskSubType === 'recognition'}
                onClick={handleFeedbackTypeCheckboxes}
              />
              <NeuCheckbox
                id="Filter-Modal-Status-Complete-Checkbox"
                className="mt-0"
                label="Coaching"
                name="coaching"
                color="blue"
                checked={filterObject.taskSubType === 'coaching'}
                onClick={handleFeedbackTypeCheckboxes}
              />
            </StatusContainer>
          )}
          {filterType !== 'feedback' ? (
            <>
              <InputHeading>Attachments & Updates</InputHeading>
              <StatusContainer>
                {!(location === 'csc' || location === 'cned') &&
                  (filterType === 'stoplights' ? (
                    <NeuCheckbox
                      data-testid="filter-modal-status-has-issues-checkbox"
                      className="mt-0"
                      label="Has attached issues"
                      name="hasAttachedIssues"
                      checked={filterObject.hasAttachedIssues}
                      color="blue"
                      onNeuChange={() => handleCheckboxes('hasAttachedIssues')}
                    />
                  ) : (
                    <NeuCheckbox
                      data-testid="filter-modal-status-attached-to-stoplight-checkbox"
                      className="mt-0"
                      label="Attached to Stoplight"
                      name="attachedToStoplight"
                      checked={filterObject.attachedToStoplight}
                      color="blue"
                      onNeuChange={() =>
                        handleCheckboxes('attachedToStoplight')
                      }
                    />
                  ))}
                <NeuCheckbox
                  data-testid="filter-modal-status-has-an-update-checkbox"
                  className="mt-0"
                  label="Has an update"
                  name="hasUpdate"
                  checked={filterObject.hasUpdate}
                  color="blue"
                  onNeuChange={() => handleCheckboxes('hasUpdate')}
                />
              </StatusContainer>
            </>
          ) : (
            ''
          )}
        </>
      )}
    </FilterCard>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    adminUserFilter: state.AdminReducer.adminUserFilter,
    censusReportFilter: state.ReportReducer.censusReportFilter,
    complianceReportFilter: state.ReportReducer.complianceReportFilter,
    employeeShiftStatus: state.EmployeeReducer.shiftStatusFilter,
    employeeSummaryReportFilter:
      state.ReportReducer.employeeSummaryReportFilter,
    feedbackFilter: state.TaskReducer.feedbackFilter,
    frequencyReportFilter: state.ReportReducer.frequencyReportFilter,
    issueFilter: state.TaskReducer.issueFilter,
    location: state.UserReducer.userSection,
    patientSummaryReportFilter: state.ReportReducer.patientSummaryReportFilter,
    roundsReportFilter: state.ReportReducer.roundsReportFilter,
    stoplightFilter: state.StoplightReducer.stoplightFilter,
    taskCategories: state.TaskReducer.categories
  };
};

export default connect(mapReduxStateToProps)(FilterModal);
